/**
 * A video.js plugin.
 *
 * In the plugin function, the value of `this` is a video.js `Player`
 * instance. You cannot rely on the player being in a "ready" state here,
 * depending on how the plugin is invoked. This may or may not be important
 * to you; if not, remove the wait for "ready"!
 */

import videojs from 'video.js';

const registerPlugin = videojs.registerPlugin || videojs.plugin;

// Default options for the plugin.
const defaults = {
  title: '',
  description: '',
  base_on_stream: false,
  online_viewers: false
};

const initTopPanelLink = (link, title) => {
    var el = document.createElement('a');
        el.href = link;
        el.target = "_blank";
        el.setAttribute('class', 'vjs-tp-title-link');
        el.innerHTML = title;
        return el;
};

const initTopPanel = (player, options) => {
  var parent = videojs.getComponent('Component'),
        top_panel = videojs.extend(parent, {}),
        title = videojs.extend(parent, {}),
        description = videojs.extend(parent, {});

    parent.registerComponent('TopPanel', top_panel);
    parent.registerComponent('VideoTitle', title);
    parent.registerComponent('VideoDescription', description);
    
    top_panel = player.addChild('TopPanel'); // TODO deprecated addClild, use new compClass() and initChildren() ?
    top_panel.addClass('vjs-top-panel');

    title = top_panel.addChild('VideoTitle');
    title.addClass('vjs-tp-title');
    if (options.link) {
       title.el().appendChild(initTopPanelLink(options.link, options.title));
    } else {
        title.el().innerHTML = options.title;
    }

    description = top_panel.addChild('VideoDescription');
    description.addClass('vjs-tp-description');
    description.el().innerHTML = options.description;

    if (options.durationDisplay) {
        top_panel.addChild('DurationDisplay');
    }
}

const initBottomPanel = (player, options) => {
    var control_bar = player.getChild('controlBar'),
        control_bar_el = control_bar && control_bar.el(),
        progress_control = control_bar && control_bar.getChild('progressControl'),
        progress_control_el = progress_control && progress_control.el();

    if (control_bar_el) {
        if (progress_control_el) {
            control_bar_el.insertBefore(progress_control_el, control_bar_el.firstChild);
        } else {
            control_bar.addClass('vjs-control-bar_oneline');
        }
    }
}

const initAdAddons = (player, options) => {
    let top_panel = player.getChild('TopPanel');

    player.on('vast.adBlockStart', function () {
        top_panel && top_panel.hide();
    });
    player.on('vast.adBlockEnd', function () {
        top_panel && top_panel.show();
    });
}

const initSmallSize = (player, options) => {
    let points = [150, 250, 300, 350, 400, 480, 600, 640];
    let cur_point;

    let configSize = () => {
        var p_el = player.el();
        if (!p_el) return;

        player.removeClass('vjs-size-w' + cur_point);
        for (let point of points) {
            if (p_el.clientWidth < point) {
                player.addClass('vjs-size-w' + point);
                cur_point = point;
                break;
            }
        }
    };

    player.on('playerresize', configSize);
    player.on('fullscreenchange', () => {
        setTimeout(() => {
            configSize();
        }, 300);
    });
    configSize();
    player.on('ready', () => configSize());
};

const initIframeSize = (player, options) => {
    if (window.parent && window.location != window.parent.location) {
        player.addClass('vjs-default-skin_desktop');
    }
}

const initOnlineViewers = (player, options) => {
    if (!options.online_viewers) return;
    let parent = videojs.getComponent('Component'),
        top_panel = player.getChild('TopPanel'),
        online_viewers = videojs.extend(parent, {});
    parent.registerComponent('OnlineViewers', online_viewers);
    online_viewers = top_panel.addChild('OnlineViewers');
    online_viewers.addClass('vjs-tp-online-viewers');
    online_viewers.setViewers = function(count) {
        let localized_val = player.localize('Viewers');
        if (typeof localized_val == 'function') {
            localized_val = localized_val(count);
        }
        this.el_.innerHTML = `${count} ${localized_val}`;
    };
};

const initBackgroundColor = (player, options) => {
    const { background_color } = options;
    if (!background_color) {
        return;
    }
    player.el().style.backgroundColor = background_color;
    player.tech(0).el().style.backgroundColor = background_color;

    const posterImage = player.getChild('PosterImage');
    if (posterImage) {
        posterImage.el().style.backgroundColor = background_color;
    }
}

/**
 * Function to invoke when the player is ready.
 *
 * This is a great place for your plugin to initialize itself. When this
 * function is called, the player will have its DOM and child components
 * in place.
 *
 * @function onPlayerReady
 * @param    {Player} player
 * @param    {Object} [options={}]
 */

const onPlayerReady = (player, options) => {
    initTopPanel(player, options);
    initBottomPanel(player, options);
    initAdAddons(player, options);
    initSmallSize(player, options);
    initIframeSize(player, options);
    initOnlineViewers(player, options);
    initBackgroundColor(player, options);
    
    let is_live_tmp = false;
    player.on('timeupdate', () => {
        let is_live = (player.duration() == Infinity && !options.isIPad) ||
            (player.hls_ && player.hls_.levels && player.hls_.levels[player.hls_.currentLevel] &&
            player.hls_.levels[player.hls_.currentLevel].details && player.hls_.levels[player.hls_.currentLevel].details.live && !options.isIPad) ||
            // Check quality changing
            (player.hls_ && player.hls_.currentLevel === -1 && player.hls_.levels && player.hls_.levels.length && is_live_tmp);
        is_live_tmp = is_live;
        if (is_live) {
            player.addClass('vjs-live-hidden-time');
        } else {
            player.removeClass('vjs-live-hidden-time');
        }
    });
};

const mainSkin = function(options) {
  this.ready(() => {
    onPlayerReady(this, videojs.mergeOptions(defaults, options));
  });
};

// Register the plugin with video.js.
registerPlugin('mainSkin', mainSkin);

export default mainSkin;

// Include the version number.
//mainSkin.VERSION = '__VERSION__';

